import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import type { AccountInfo, UserInfo } from '@/api-clients/dashboard-api';
import {
  getAccountInfo,
  getAccountUsers,
  getGroupsWithAllocations as apiGetGroupsWithAllocations,
  addUsersToGroups as apiAddUsersToGroups
} from '@/helpers/apiDashboardWrappers';

export interface AccountData {
  isLoading: boolean;
  content?: AccountInfo;
}

export const useAccountStore = defineStore('account', () => {
  const currentAccount = ref<AccountData>({ isLoading: false });
  const getCurrentAccountPromise = ref<Promise<AccountInfo>>();
  const accountUsersLocal = ref<UserInfo[]>([]);
  const usersLoadingLocal = ref(false);

  async function refreshCurrentAccount() {
    currentAccount.value = { isLoading: true };
    try {
      getCurrentAccountPromise.value = getAccountInfo();
      currentAccount.value.content = await getCurrentAccountPromise.value;
    }
    catch {
      currentAccount.value.content = undefined;
    }
    finally {
      currentAccount.value.isLoading = false;
    }
  }

  async function getCurrentAccount() {
    if (!currentAccount.value.isLoading)
      return currentAccount.value.content;

    try {
      if (!getCurrentAccountPromise.value) {
        getCurrentAccountPromise.value = getAccountInfo();
      }

      return await getCurrentAccountPromise.value;
    }
    catch {
      return undefined;
    }
  }

  async function refresh() {
    await refreshCurrentAccount();

    usersLoadingLocal.value = true;
    try {
      accountUsersLocal.value = await getAccountUsers();
    }
    catch {
      accountUsersLocal.value = [];
    }
    finally {
      usersLoadingLocal.value = false;
    }
  }

  async function getGroupsWithAllocations() {
    try {
      return await apiGetGroupsWithAllocations();
    }
    catch {
      return [];
    }
  }

  async function addUsersToGroups(usersGroups: Map<string, string>) {
    return await apiAddUsersToGroups(usersGroups);
  }

  return {
    currentAccount: computed(() => currentAccount.value),
    accountUsers: computed(() => accountUsersLocal.value),
    areUsersLoading: computed(() => usersLoadingLocal.value),
    refresh,
    getGroupsWithAllocations,
    addUsersToGroups,
    getCurrentAccount
  };
});
